import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HiArrowNarrowRight } from "react-icons/hi";
import { FiChevronDown } from "react-icons/fi";
import {
  closeCart,
  removeFromCart,
  increaseQty,
  decreaseQty,
  loadCart,
} from "../../feature/cartSlice";

const formatPrice = (n) => `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// demo codes only - real codes must be validated on your backend
const COUPONS = { WELCOME10: 10 };

function CartDrawer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, isOpen } = useSelector((state) => state.cart);

  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(0);
  const [codeMsg, setCodeMsg] = useState("");

  const totalItems = items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const discount = (subtotal * percent) / 100;
  const total = subtotal - discount;

  // lock page scroll + close on Escape
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && dispatch(closeCart());
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, dispatch]);

  // logged-in user: refresh the cart from the server every time the drawer opens
  useEffect(() => {
    if (isOpen && localStorage.getItem("token")) dispatch(loadCart());
  }, [isOpen, dispatch]);

  const applyCode = () => {
    const p = COUPONS[code.trim().toUpperCase()];
    if (p) {
      setPercent(p);
      setCodeMsg(`${p}% discount applied`);
    } else {
      setPercent(0);
      setCodeMsg("Invalid discount code");
    }
  };

  const handleCheckout = () => {
    dispatch(closeCart());
    // guests can fill the cart, checkout needs a login
    if (!localStorage.getItem("token")) {
      navigate("/login", { state: { from: "/checkout" } }); // change "/login" if your route differs
      return;
    }
    navigate("/checkout"); // change to your checkout route
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => dispatch(closeCart())}
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-[70px] border-b border-gray-200 shrink-0">
          <button
            type="button"
            onClick={() => dispatch(closeCart())}
            aria-label="Close cart"
            className="text-[26px] text-[#111] hover:text-[#1f5a2e] transition-colors"
          >
            <HiArrowNarrowRight />
          </button>
          <h2 className="text-[20px] sm:text-[22px] font-semibold text-[#111]">
            Review Your Cart
          </h2>
          <span className="min-w-[32px] h-[32px] px-2 flex items-center justify-center bg-[#f0f0f3] text-[13px] rounded-[3px]">
            {totalItems}
          </span>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <p className="text-[16px] font-medium text-[#111]">Your cart is empty</p>
              <button
                type="button"
                onClick={() => {
                  dispatch(closeCart());
                  navigate("/shop");
                }}
                className="mt-4 h-[42px] px-8 bg-[#1f5a2e] hover:bg-[#174a24] text-white text-[12px] font-bold uppercase tracking-[1px] rounded-[2px] transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={`${item.id}-${item.size ?? ""}`}
                className="flex gap-4 px-5 py-5 border-b border-gray-200"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-[75px] h-[75px] object-cover bg-[#f5f5f7] rounded-[3px] shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <h4 className="text-[16px] text-[#1f5a2e] leading-snug line-clamp-2">
                    {item.name}
                  </h4>
                  {item.size && (
                    <p className="text-[12px] text-gray-500 mt-1">Size: {item.size}</p>
                  )}
                  <p className="text-[14px] text-[#111] mt-2">{formatPrice(item.price)}</p>
                  <button
                    type="button"
                    onClick={() => dispatch(removeFromCart(item))}
                    className="mt-2 text-[12px] uppercase tracking-[2px] text-[#1f5a2e] underline hover:no-underline"
                  >
                    Remove
                  </button>
                </div>

                {/* Vertical qty stepper */}
                <div className="flex flex-col border border-[#ddd] self-start shrink-0">
                  <button
                    type="button"
                    onClick={() => dispatch(increaseQty(item))}
                    disabled={item.stock != null && item.qty >= item.stock}
                    aria-label="Increase quantity"
                    className="w-[38px] h-[32px] text-[16px] hover:bg-gray-100"
                  >
                    +
                  </button>
                  <span className="w-[38px] h-[32px] flex items-center justify-center text-[14px] border-y border-[#ddd]">
                    {item.qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => dispatch(decreaseQty(item))}
                    disabled={item.qty <= 1}
                    aria-label="Decrease quantity"
                    className="w-[38px] h-[32px] text-[16px] hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    −
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="shrink-0 shadow-[0_-6px_12px_-8px_rgba(0,0,0,0.15)]">
            {/* Discount code */}
            <button
              type="button"
              onClick={() => setShowCode((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-[14px] text-[#111] border-b border-gray-200"
            >
              Got a Discount Code?
              <FiChevronDown
                className={`text-[20px] transition-transform ${showCode ? "rotate-180" : ""}`}
              />
            </button>
            {showCode && (
              <div className="px-5 py-3 border-b border-gray-200">
                <div className="flex gap-2">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter code"
                    className="flex-1 h-[40px] border border-[#ddd] px-3 text-[14px] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={applyCode}
                    className="h-[40px] px-5 bg-[#111] text-white text-[11px] font-bold uppercase tracking-[1px]"
                  >
                    Apply
                  </button>
                </div>
                {codeMsg && (
                  <p className={`mt-2 text-[12px] ${percent ? "text-green-600" : "text-red-500"}`}>
                    {codeMsg}
                  </p>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="flex justify-between px-5 py-4 text-[16px] font-semibold border-b border-gray-200">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between px-5 py-4 text-[16px] font-semibold border-b border-gray-200">
              <span>Discount</span>
              <span>{formatPrice(discount)}</span>
            </div>
            <div className="flex justify-between px-5 py-4 text-[16px] font-semibold border-b border-gray-200">
              <span>Total</span>
              <span>
                {discount > 0 && (
                  <span className="line-through text-gray-400 mr-2">{formatPrice(subtotal)}</span>
                )}
                {formatPrice(total)}
              </span>
            </div>

            {/* Checkout */}
            <div className="p-5">
              <button
                type="button"
                onClick={handleCheckout}
                className="w-full h-[60px] flex items-center justify-center gap-2 bg-[#1f5a2e] hover:bg-[#174a24] transition-colors text-white text-[18px] font-medium rounded-[6px]"
              >
                Proceed to Checkout <HiArrowNarrowRight className="text-[22px]" />
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

export default CartDrawer;